import { damageControlApi } from "@/api/damageControl";

const DEVICE_REASONS = new Set(["blurry", "too_dark", "overexposed", "wrong_zone", "obstructed", "too_far"]);
const UPLOAD_FAILED_KEY = "damage_control.capture.upload_failed";

const isObject = (value) => value !== null && typeof value === "object";
const isDeviceRefusal = (reasons) => Array.isArray(reasons) && reasons.some((reason) => DEVICE_REASONS.has(reason));

// The first two device refusals stay on the device. The third is uploaded with its reason.
export function shouldUploadAttempt({ attempt, reasons } = {}) {
  return !isDeviceRefusal(reasons) || attempt >= 3;
}

function shotFrom(value) {
  if (!isObject(value)) return null;
  return value.latest_shot || value.current_shot || value.shot || (Number.isInteger(value.series) ? value : null);
}

function chronologicalNewest(shots) {
  return shots.filter((shot) => Number.isInteger(shot.series) && shot.series > 0)
    .sort((a, b) => b.series - a.series || (b.attempt || 0) - (a.attempt || 0))[0] || null;
}

// Returns the next API sequence position for a zone. Rejections stay in their series;
// retaking an accepted current shot starts a new series.
export function nextSeriesAttempt(zone, history = []) {
  const zoneShot = shotFrom(zone);
  const historyShot = chronologicalNewest(Array.isArray(history) ? history.map(shotFrom).filter(Boolean) : []);
  const historyIsLatest = historyShot && (!zoneShot || historyShot.series > zoneShot.series ||
    (historyShot.series === zoneShot.series && historyShot.attempt >= zoneShot.attempt));
  const latest = historyIsLatest ? historyShot : zoneShot;
  if (!latest) return { series: 1, attempt: 1 };

  if (latest.state === "pending") {
    const error = new Error("A pending shot must finish before another attempt");
    error.code = "pending_shot";
    throw error;
  }

  const refused = latest.state === "rejected" || latest.state === "device_rejected";
  if (refused) return { series: latest.series, attempt: Math.min(3, (latest.attempt || 0) + 1) };

  const currentShot = isObject(zone) && (zone.current_shot || zone.current);
  const hasCurrent = Boolean(currentShot || latest.current);
  return hasCurrent ? { series: latest.series + 1, attempt: 1 } : { series: latest.series, attempt: Math.min(3, (latest.attempt || 0) + 1) };
}

function failedUploadError(cause) {
  const error = new Error(UPLOAD_FAILED_KEY);
  error.i18nKey = UPLOAD_FAILED_KEY;
  error.code = "upload_failed";
  if (cause) error.cause = cause;
  return error;
}

function isAttemptSeriesValidation(error) {
  const data = error && error.response && error.response.data;
  if (!isObject(data)) return false;
  const bodyError = isObject(data.error) ? data.error : null;
  const code = bodyError ? bodyError.code : data.error;
  return error.response.status === 422 && code === "validation_failed";
}

export function uploadTarget(target, blob, fetchImpl = globalThis.fetch, signal, failureMessage = "Signed upload failed") {
  return fetchImpl(target.url, {
    method: "PUT",
    headers: target.headers,
    body: blob,
    ...(signal === undefined ? {} : { signal }),
  }).then((response) => {
    if (!response || response.ok !== true) throw new Error(failureMessage);
    return response;
  });
}

// The candidate owns the upload token once both PUTs succeed. A lost register
// response must never turn the same photograph into a new upload/attempt.
export async function uploadShot(axios, candidate, { fetchImpl = globalThis.fetch, signal } = {}) {
  const { inspectionId, zoneKey, original, preview, capturedAt, reasons = [] } = candidate;
  if (!candidate.preparedUpload) {
    const prepared = await damageControlApi.prepareUpload(axios, inspectionId, {
      zone_key: zoneKey,
      original: { content_type: original.type, size: original.size },
      preview: { content_type: preview.type, size: preview.size },
    });
    throwIfAborted(signal);
    try {
      await Promise.all([
        uploadTarget(prepared.original, original, fetchImpl, signal),
        uploadTarget(prepared.preview, preview, fetchImpl, signal),
      ]);
    } catch (error) {
      throwIfAborted(signal);
      throw failedUploadError(error);
    }
    candidate.preparedUpload = prepared;
  }

  const register = async () => {
    throwIfAborted(signal);
    return damageControlApi.registerShot(axios, inspectionId, {
      upload_token: candidate.preparedUpload.upload_token,
      attempt: candidate.attempt, series: candidate.series,
      captured_at: capturedAt, reason: reasons,
    });
  };
  const refreshedZone = async (error) => {
    try {
      const payload = await damageControlApi.getInspection(axios, inspectionId, { signal });
      throwIfAborted(signal);
      return (payload.zones || []).find(zone => zone.key === zoneKey);
    } catch (_) {
      throwIfAborted(signal);
      throw error;
    }
  };
  const reconcile = async (error) => {
    throwIfAborted(signal);
    if (error.response && error.response.status !== 409) throw error;
    const zone = await refreshedZone(error);
    const latest = zone && zone.latest_shot;
    if (latest && Number(latest.id) > Number(candidate.previousShotId || 0)) return latest;
    throw error;
  };
  try { return await register(); }
  catch (error) {
    if (!isAttemptSeriesValidation(error)) return reconcile(error);
    if (candidate.registrationRecalculated) throw error;
    candidate.registrationRecalculated = true;
    const zone = await refreshedZone(error);
    if (!zone) throw error;
    let sequence;
    try { sequence = nextSeriesAttempt(zone); } catch (_) { throw error; }
    if (sequence.series === candidate.series && sequence.attempt === candidate.attempt) throw error;
    Object.assign(candidate, sequence);
    try { return await register(); }
    catch (retryError) { return reconcile(retryError); }
  }
}

function abortError() {
  const error = new Error("The operation was aborted");
  error.name = "AbortError";
  return error;
}

function throwIfAborted(signal) {
  if (signal && signal.aborted) throw abortError();
}

function pollTimeout() {
  return Object.assign(new Error("Inspection poll timed out"), {
    code: "timeout", i18nKey: "damage_control.capture.poll_timeout",
  });
}

function waitUntilVisible(signal, deadline) {
  return new Promise((resolve, reject) => {
    throwIfAborted(signal);
    const finish = (error) => {
      clearTimeout(timer);
      document.removeEventListener("visibilitychange", changed);
      if (signal) signal.removeEventListener("abort", aborted);
      if (error) reject(error);
      else resolve();
    };
    const changed = () => { if (!document.hidden) finish(); };
    const aborted = () => finish(abortError());
    const timer = setTimeout(() => finish(pollTimeout()), Math.max(0, deadline - Date.now()));
    document.addEventListener("visibilitychange", changed);
    if (signal) signal.addEventListener("abort", aborted, { once: true });
    changed();
  });
}

function abortableDelay(ms, signal) {
  return new Promise((resolve, reject) => {
    throwIfAborted(signal);
    const timer = setTimeout(done, ms);
    function done() {
      if (signal) signal.removeEventListener("abort", abort);
      resolve();
    }
    function abort() {
      clearTimeout(timer);
      signal.removeEventListener("abort", abort);
      reject(abortError());
    }
    if (signal) signal.addEventListener("abort", abort, { once: true });
  });
}

function timedInspectionRequest(axios, id, signal, ms) {
  return new Promise((resolve, reject) => {
    throwIfAborted(signal);
    let settled = false;
    const requestController = new AbortController();
    const finish = (callback, value) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      if (signal) signal.removeEventListener("abort", onAbort);
      callback(value);
    };
    const onAbort = () => {
      requestController.abort();
      finish(reject, abortError());
    };
    const timer = setTimeout(() => {
      requestController.abort();
      finish(reject, pollTimeout());
    }, ms);
    if (signal) signal.addEventListener("abort", onAbort, { once: true });
    damageControlApi.getInspection(axios, id, { signal: requestController.signal })
      .then((payload) => finish(resolve, payload), (error) => finish(reject, error));
  });
}

export async function pollInspection(axios, id, {
  until, intervalMs, timeoutMs = 15000, signal, onUpdate,
} = {}) {
  const deadline = Date.now() + timeoutMs;
  let delayMs = intervalMs === undefined ? 1000 : intervalMs;
  while (Date.now() < deadline) {
    throwIfAborted(signal);
    if (typeof document !== "undefined" && document.hidden) await waitUntilVisible(signal, deadline);
    throwIfAborted(signal);
    if (Date.now() >= deadline) break;
    let payload;
    try {
      payload = await timedInspectionRequest(axios, id, signal, Math.max(1, deadline - Date.now()));
    } catch (error) {
      if (error.name === "AbortError") throw error;
      if (error.code === "timeout" || Date.now() >= deadline) {
        throw pollTimeout();
      }
      throw error;
    }
    if (onUpdate) onUpdate(payload);
    if (until && until(payload)) return payload;
    const remaining = deadline - Date.now();
    if (remaining <= 0) break;
    await abortableDelay(Math.min(delayMs, remaining), signal);
    if (intervalMs === undefined) delayMs = Math.min(3000, delayMs + 1000);
  }
  throw pollTimeout();
}
